import {
  EmptyState,
  FlexColumn,
  FlexRow,
  Loading,
  MaterialIcon,
  StyledSearchBar,
  useDebouncedSearch,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { LabelMedium } from 'baseui/typography';
import { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { useCamera } from '../../../hooks/useCamera';
import { useDebounceFunction } from '../../../hooks/useDebounce';
import { useStyles } from '../../../hooks/useStyles';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import {
  fleetHealtEventIdsState,
  fleetHealtEventsPageState,
  fleetHealthEventsState,
  loadingFleetHealthEventsState,
  searchFleetHealtEventState,
} from '../../../storage/fleetHealthEventsStates';
import { getValidPositions } from '../../../utils/fleetHealth';
import { FloatingCardContent } from '../../layout/cards/FloatingCardContent';
import StyledTooltip from '../../ui/StyledTooltip';
import FleetHealthEventCard from './FleetHealthEventCard';
import {
  fleetHealthClientState,
  fleetHealthConfigurationState,
  fleetHealthEventTypeState,
  fleetHealthSubClientState,
} from '../../../storage/fleetHealthStates';
import FleetHealthEventFilters from './FleetHealthEventFilters';

function FleetHealthEventList() {
  const { theme, css } = useStyles();
  const eventIds = useRecoilValue(fleetHealtEventIdsState);
  const loadingEvents = useRecoilValue(loadingFleetHealthEventsState);
  const { search, setSearch } = useDebouncedSearch(useRecoilState(searchFleetHealtEventState));
  const paginatedEvents = useRecoilValue(fleetHealthEventsState);
  const [eventsPage, setEventsPage] = useRecoilState(fleetHealtEventsPageState);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const [initialZoom, setInitialZoom] = useState(false);
  const { fitToLngLats } = useCamera();
  const total = paginatedEvents?.totalEvents ?? 0;
  const totalProcessed = paginatedEvents?.totalProcessed ?? 0;
  const totalToClose = paginatedEvents?.totalToClose ?? 0;
  const resetSelectType = useResetRecoilState(fleetHealthEventTypeState);
  const resetSelectConfiguration = useResetRecoilState(fleetHealthConfigurationState);
  const resetSelectClient = useResetRecoilState(fleetHealthClientState);
  const resetSelectSubClient = useResetRecoilState(fleetHealthSubClientState);

  useUpdateEffect(() => {
    if (paginatedEvents && !initialZoom) {
      fitToLngLats(getValidPositions(paginatedEvents.items));
      setInitialZoom(true);
    }
  }, [paginatedEvents]);

  const onUpdate = () => {
    setEventsPage(0);
  };

  useEffect(() => {
    if (!scrollableRef.current) return;
    if (eventsPage === 0) {
      const scrollableDiv = scrollableRef.current;
      scrollableDiv.scrollTo(0, 0);
    }
  }, [eventsPage, scrollableRef]);

  const fetchMoreData = useDebounceFunction(function () {
    setEventsPage(eventsPage + 1);
  }, 500);

  const resetFilters = () => {
    setSearch('');
    resetSelectType();
    resetSelectConfiguration();
    resetSelectClient();
    resetSelectSubClient();
  };

  return (
    <FloatingCardContent
      flexible
      gap={theme.sizing.scale500}
    >
      {!eventIds && !loadingEvents && <EmptyState title="No cuentas con ningún evento." />}
      {!eventIds && loadingEvents && <Loading />}
      {eventIds && (
        <>
          <FlexRow gap={theme.sizing.scale300}>
            <div className={css({ flex: '1', display: 'flex', alignItems: 'center' })}>
              <StyledSearchBar
                name="event-search"
                value={search}
                onChange={setSearch}
                clearable
                noMargin
              />
              <FleetHealthEventFilters resetFilters={resetFilters} />
              <StyledTooltip
                content={'Limpiar filtros'}
                showArrow={false}
              >
                <Button
                  size="mini"
                  shape="round"
                  onClick={resetFilters}
                  kind="tertiary"
                  isLoading={loadingEvents}
                >
                  <MaterialIcon
                    name={'clear'}
                    size="mini"
                  />
                </Button>
              </StyledTooltip>
            </div>
            <StyledTooltip
              content={'Actualizar'}
              showArrow={false}
            >
              <Button
                size="compact"
                shape="square"
                onClick={onUpdate}
                kind="tertiary"
              >
                <MaterialIcon name={'sync'} />
              </Button>
            </StyledTooltip>
          </FlexRow>
          {!!paginatedEvents?.items.length && (
            <div
              id="scrollableDiv"
              className={css({ height: '100%', overflow: 'auto' })}
              ref={scrollableRef}
            >
              <InfiniteScroll
                dataLength={paginatedEvents?.items.length}
                next={fetchMoreData}
                hasMore={paginatedEvents?.hasNext ?? false}
                loader={<Loading />}
                endMessage={
                  paginatedEvents?.hasNext && (
                    <p style={{ textAlign: 'center' }}>
                      <b>Cargando...</b>
                    </p>
                  )
                }
                scrollableTarget="scrollableDiv"
                style={{
                  overflow: 'hidden',
                }}
              >
                <FlexColumn gap={'8px'}>
                  {eventIds.map((eventId) => (
                    <FleetHealthEventCard
                      id={eventId}
                      key={eventId}
                    />
                  ))}
                </FlexColumn>
              </InfiniteScroll>
            </div>
          )}
          {!eventIds.length && (
            <EmptyState
              title="No se encontraron resultados"
              description="Intenta con otra busqueda"
            />
          )}
          <FlexRow
            classNames={css({
              background: theme.colors.backgroundSecondary,
              paddingTop: theme.sizing.scale300,
              paddingBottom: theme.sizing.scale300,
              paddingRight: theme.sizing.scale600,
              paddingLeft: theme.sizing.scale600,
              borderRadius: theme.sizing.scale300,
              alignItems: 'center',
              justifyContent: 'center',
            })}
          >
            <FlexRow classNames={css({ justifyContent: 'space-between', width: '100%' })}>
              <LabelMedium>Total: {total}</LabelMedium>
              <LabelMedium>En progreso: {totalProcessed}</LabelMedium>
              <LabelMedium>Por cerrar: {totalToClose}</LabelMedium>
            </FlexRow>
          </FlexRow>
        </>
      )}
    </FloatingCardContent>
  );
}

export default FleetHealthEventList;
